import {
	type BoundActions,
	createActionsHook,
	createHook,
	createStore,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

export type ExtensionsState = {
	isInAutoFocusMode: boolean; // Defines if it should auto scroll to expanded card
	isInViewMode: boolean; // Defines if the extension is in view mode
	selectedExtensionLocalId: string | null; // In edit mode defines an active extension localId
	selectedToEditCardId: string | null; // In edit mode defines an active card id (selected on the extension and expanded on the config panel)
	selectedToRemoveCardId: string | null; // In edit mode defines an active card id that is about to be removed
};

const initialState: ExtensionsState = {
	isInViewMode: true,
	isInAutoFocusMode: true,
	selectedExtensionLocalId: null,
	selectedToEditCardId: null,
	selectedToRemoveCardId: null,
};

// Actions for managing the Custom Site Extensions state that is shared between the extension and the config panel
const actions = {
	setIsInViewMode:
		(isInViewMode: boolean) =>
		({ setState }: StoreActionApi<ExtensionsState>): void => {
			setState({ isInViewMode });
		},
	setIsInAutoFocusMode:
		(isInAutoFocusMode: boolean) =>
		({ setState }: StoreActionApi<ExtensionsState>): void => {
			setState({ isInAutoFocusMode });
		},
	setSelectedExtensionLocalId:
		(extensionLocalId: string | null) =>
		({ getState, setState }: StoreActionApi<ExtensionsState>): void => {
			if (getState().isInViewMode) {
				return;
			}
			setState({ selectedExtensionLocalId: extensionLocalId });
		},
	setSelectedToEditCardId:
		(cardId: string | null) =>
		({ getState, setState }: StoreActionApi<ExtensionsState>): void => {
			if (getState().isInViewMode) {
				return;
			}
			setState({ selectedToEditCardId: cardId });
		},
	setSelectedToRemoveCardId:
		(cardId: string | null) =>
		({ getState, setState }: StoreActionApi<ExtensionsState>): void => {
			if (getState().isInViewMode) {
				return;
			}
			setState({ selectedToRemoveCardId: cardId });
		},
};

const ExtensionsStateStore = createStore({
	initialState,
	actions,
	name: 'ExtensionsStateStore',
});

export const useExtensionsState: HookFunction<
	ExtensionsState,
	BoundActions<
		ExtensionsState,
		{
			setIsInAutoFocusMode: (
				isInAutoFocusMode: boolean,
			) => ({ setState }: StoreActionApi<ExtensionsState>) => void;
			setIsInViewMode: (
				isInViewMode: boolean,
			) => ({ setState }: StoreActionApi<ExtensionsState>) => void;
			setSelectedExtensionLocalId: (
				extensionLocalId: string | null,
			) => ({ getState, setState }: StoreActionApi<ExtensionsState>) => void;
			setSelectedToEditCardId: (
				cardId: string | null,
			) => ({ getState, setState }: StoreActionApi<ExtensionsState>) => void;
			setSelectedToRemoveCardId: (
				cardId: string | null,
			) => ({ getState, setState }: StoreActionApi<ExtensionsState>) => void;
		}
	>,
	void
> = createHook(ExtensionsStateStore);
export const useExtensionsActions: HookActionsFunction<
	BoundActions<
		ExtensionsState,
		{
			setIsInAutoFocusMode: (
				isInAutoFocusMode: boolean,
			) => ({ setState }: StoreActionApi<ExtensionsState>) => void;
			setIsInViewMode: (
				isInViewMode: boolean,
			) => ({ setState }: StoreActionApi<ExtensionsState>) => void;
			setSelectedExtensionLocalId: (
				extensionLocalId: string | null,
			) => ({ getState, setState }: StoreActionApi<ExtensionsState>) => void;
			setSelectedToEditCardId: (
				cardId: string | null,
			) => ({ getState, setState }: StoreActionApi<ExtensionsState>) => void;
			setSelectedToRemoveCardId: (
				cardId: string | null,
			) => ({ getState, setState }: StoreActionApi<ExtensionsState>) => void;
		}
	>
> = createActionsHook(ExtensionsStateStore);
